import { Observable } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

import { Component, OnInit, Inject } from '@angular/core';

import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormGroup, FormControl, FormBuilder, Validators } from '@angular/forms';

import { AngularFirestore, AngularFirestoreCollection } from '@angular/fire/compat/firestore';

import { moveItemInArray, CdkDragDrop } from "@angular/cdk/drag-drop";

import { SpinnerService } from '../../services/spinner.service';
import { ApiExhibitDetail, ApiSetExhibitDetailsRequest, ApiFile, SettingFile } from '../../api-client-module';
import { ExhibitDetailAttribute } from '../../firestore-models/exhibit-detail-attribute';
import { UploadedFile } from '../../firestore-models/uploaded-file';
import { ProjectLanguage } from '../../firestore-models/project-language';

import { ConfigurationService } from '../../configuration.service';
import { ApiClientService } from '../../api-client.service';
import { AppConst } from '../../app-const';
import { MultiNameDialogComponent } from '../../common/multi-name-dialog/multi-name-dialog.component';
import { MultiDescDialogComponent } from '../../common/multi-desc-dialog/multi-desc-dialog.component';
import { MultiYoutubeDialogComponent } from '../../common/multi-youtube-dialog/multi-youtube-dialog.component';
import { MultiExternallinkDialogComponent } from '../../common/multi-externallink-dialog/multi-externallink-dialog.component';
import { SelectUploadedFileDialogComponent } from '../../select-uploaded-file-dialog/select-uploaded-file-dialog.component';
import { TranslateService } from '@ngx-translate/core';

export interface ExhibitDetailDialogData {
  change_type: number;
  exhibitDetail?: ApiExhibitDetail;
}

export interface marker {
	lat: number;
	lng: number;
	draggable: boolean;
}

@Component({
  selector: 'app-exhibit-detail-dialog',
  templateUrl: './exhibit-detail-dialog.component.html',
  styleUrls: ['./exhibit-detail-dialog.component.css'],
  standalone: false,
})
export class ExhibitDetailDialogComponent implements OnInit {

  //展示情報
  exhibitDetail?: ApiExhibitDetail;
  //各展示情報に紐付くプロジェクトID
  projectId: string;

  isAdded: boolean = false;
  projectLanguages!: Array<ProjectLanguage>;

  //展示情報フォーム
  exhibitDetailFormGroup!: FormGroup;
  exhibitDetailIdControl!: FormControl;
  exhibitDetailAttributeIdControl!: FormControl;
  autoTransContral!: FormControl;
  exhibitDetailNamesControl!: FormControl;
  exhibitDetailDescsControl!: FormControl;
  youtubeCodesControl!: FormControl;
  useYoutubeControl!: FormControl;
  externallinkNamesControl!: FormControl;
  externallinkUrlsControl!: FormControl;
  useExternallinkControl!: FormControl;
  notOverlapGuideControl!: FormControl;
  inExhibitDetailId = '';

  private exhibitDetailAttributesCollection!: AngularFirestoreCollection<ExhibitDetailAttribute>;
  exhibitDetailAttributes!: Observable<ExhibitDetailAttribute[]>;

  exhibitDetailFiles: SettingFile[] = [];

  // ユーザータイプ
  readonly USER_TYPE_USER: number = AppConst.MANAGE_USER_TYPE_USER;
  userType!: number;
  isEdit: boolean = false;

  currentLang: string = 'ja';
  projectBaseLang: string = 'ja';

  constructor(
    public dialogRef: MatDialogRef<ExhibitDetailDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ExhibitDetailDialogData,
    public dialog: MatDialog,
    private formBuilder: FormBuilder,
    private angularFirestore: AngularFirestore,
    private configurationService: ConfigurationService,
    private translate: TranslateService,
    private apiClientService: ApiClientService,
    private spinnerService: SpinnerService,
    private toastr: ToastrService,
  ) {
    this.projectId = this.configurationService.getProjectId();
    this.userType = this.configurationService.getUserType();
    this.isEdit = this.configurationService.getIsEdit();
   }

  ngOnInit(): void {
    this.projectLanguages = this.configurationService.getProject().languages!;
    this.currentLang = this.translate.currentLang;
    this.projectBaseLang = this.configurationService.getProject().project_base_lang;
    this.exhibitDetailAttributesCollection = this.angularFirestore.collection<ExhibitDetailAttribute>(
      AppConst.FIRESTORE_EXHIBIT_DETAIL_ATTR, ref => ref.where('project_id', '==', this.projectId));
    this.exhibitDetailAttributes = this.exhibitDetailAttributesCollection.valueChanges({ idField: 'id' });

    this.exhibitDetail = this.data.exhibitDetail;
    if (!this.exhibitDetail) {
      this.exhibitDetail = {
        exhibit_detail_id: '',
        exhibit_detail_attribute_id: '',
        exhibit_detail_names: [],
        exhibit_detail_descs: null as any,
        exhibit_detail_files: [],
        youtube_codes: [],
        use_youtube: false,
        externallink_names: [],
        externallink_urls: [],
        use_externallink: false,
        not_overlap_guide: false,
        project_id: this.projectId,
        enabled: true,
      };
      this.isAdded = true;
    } else {
      this.inExhibitDetailId = this.exhibitDetail.exhibit_detail_id;
    }

    this.createForm();
    this.exhibitDetailIdControl = this.exhibitDetailFormGroup.get('exhibitDetailId') as FormControl;
    this.exhibitDetailAttributeIdControl = this.exhibitDetailFormGroup.get('exhibitDetailAttributeId') as FormControl;
    this.exhibitDetailNamesControl = this.exhibitDetailFormGroup.get('exhibitDetailNames') as FormControl;
    this.exhibitDetailDescsControl = this.exhibitDetailFormGroup.get('exhibitDetailDescs') as FormControl;
    this.youtubeCodesControl = this.exhibitDetailFormGroup.get('youtubeCodes') as FormControl;
    this.useYoutubeControl = this.exhibitDetailFormGroup.get('useYoutube') as FormControl;
    this.externallinkNamesControl = this.exhibitDetailFormGroup.get('externallinkNames') as FormControl;
    this.externallinkUrlsControl = this.exhibitDetailFormGroup.get('externallinkUrls') as FormControl;
    this.useExternallinkControl = this.exhibitDetailFormGroup.get('useExternallink') as FormControl;
    this.notOverlapGuideControl = this.exhibitDetailFormGroup.get('notOverlapGuide') as FormControl;

    this.getFiles();
  }

  //展示情報並べ替え
  onDrop(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.exhibitDetailFiles, event.previousIndex, event.currentIndex);
    let files: Array<ApiFile> = [];
    for (const file of this.exhibitDetailFiles) {
      files.push({file_id: file.id, lang_code: file.lang_code});
    }
    this.exhibitDetail!.exhibit_detail_files = files;
  }

  //以下、フォームの記入エラー表示
  public getErrorMessageToExhibitDetailId() {
    return this.exhibitDetailIdControl.hasError('required')
    ? this.translate.instant('必須項目')
    : this.exhibitDetailIdControl.hasError('pattern')
    ? this.translate.instant('半角英数字ハイフンのみ')
    : '';
  }

  public getErrorMessageToExhibitDetailNames() {
    return this.exhibitDetailNamesControl.hasError('required')
    ? this.translate.instant('必須項目')
    : '';
  }

  public getErrorMessageToExhibitDetailDescs() {
    return this.exhibitDetailDescsControl.hasError('required')
    ? this.translate.instant('必須項目')
    : '';
  }

  //フォームの値を各要素に代入
  private createForm() {
    this.exhibitDetailFormGroup = this.formBuilder.group(
      {
        exhibitDetailId: [this.exhibitDetail!.exhibit_detail_id, [Validators.required, Validators.pattern('^[a-zA-Z0-9-]+$')]],
        exhibitDetailAttributeId: [this.exhibitDetail!.exhibit_detail_attribute_id],
        exhibitDetailNames: [this.exhibitDetail!.exhibit_detail_names, [Validators.required]],
        exhibitDetailDescs: [this.exhibitDetail!.exhibit_detail_descs, [Validators.required]],
        youtubeCodes: [this.exhibitDetail!.youtube_codes, []],
        useYoutube: [this.exhibitDetail!.use_youtube, []],
        externallinkNames: [this.exhibitDetail!.externallink_names, []],
        externallinkUrls: [this.exhibitDetail!.externallink_urls, []],
        useExternallink: [this.exhibitDetail!.use_externallink, []],
        notOverlapGuide: [this.exhibitDetail!.not_overlap_guide, []],
      }
    );
  }

  //展示画像ファイル設定
  private getFiles() {
    this.exhibitDetailFiles = [];
    if (!!this.exhibitDetail!.exhibit_detail_files && this.exhibitDetail!.exhibit_detail_files.length > 0) {
      // NOTE: in で取得できるのは最大10までのため、該当プロジェクトのアップロードファイルを全て取得してから
      //       展示情報に登録されている id で絞り込む。
      this.angularFirestore.collection<UploadedFile>(
        AppConst.FIRESTORE_UPLOADED_FILES, ref => ref.where('project_id', '==', this.projectId)
        ).valueChanges({ idField: 'id' }).subscribe(docs => {
          this.exhibitDetailFiles = [];
          if (!!docs) {
            for (const exhibitDetailFile of this.exhibitDetail!.exhibit_detail_files!) {
              for (const uploadedFile of docs) {
                if (exhibitDetailFile.file_id === uploadedFile.id) {
                  const settingFile: SettingFile = {
                    id: uploadedFile.id,
                    file_name: uploadedFile.file_name,
                    download_url: uploadedFile.download_url,
                    lang_code: exhibitDetailFile.lang_code,
                  }
                  this.exhibitDetailFiles.push(settingFile);
                  break;
                }
              }
            }
          }
      });
    }
  }

  // 変更された言語を登録
  changeExhibitDetailFileLang(i: number, event: any) {
    this.exhibitDetail!.exhibit_detail_files![i].lang_code = event.value;
    this.exhibitDetailFiles![i].lang_code = event.value;
  }

  //更新ボタン
  addExhibitDetail(): void {
    if (this.exhibitDetailFormGroup.valid) {
      this.exhibitDetail!.exhibit_detail_id = this.exhibitDetailIdControl.value;
      this.exhibitDetail!.exhibit_detail_attribute_id = this.exhibitDetailAttributeIdControl.value;
      this.exhibitDetail!.exhibit_detail_names = this.exhibitDetailNamesControl.value;
      this.exhibitDetail!.exhibit_detail_descs = this.exhibitDetailDescsControl.value;
      this.exhibitDetail!.youtube_codes = this.youtubeCodesControl.value;
      this.exhibitDetail!.use_youtube = this.useYoutubeControl.value;
      this.exhibitDetail!.externallink_names = this.externallinkNamesControl.value;
      this.exhibitDetail!.externallink_urls = this.externallinkUrlsControl.value;
      this.exhibitDetail!.use_externallink = this.useExternallinkControl.value;
      this.exhibitDetail!.not_overlap_guide = this.notOverlapGuideControl.value;
      if (this.exhibitDetail) {
        let request: ApiSetExhibitDetailsRequest = {
          access_token: '',
          change_type: this.data.change_type,
          exhibit_details: [this.exhibitDetail]
        }
        this.spinnerService.openSpinner();
        this.apiClientService.setExhibitDetails(request).then(
          response => {
            if (response!.ret === AppConst.API_RET_SUCCESS) {
              this.toastr.success(this.translate.instant('登録しました。'));
              this.dialogRef.close();
            } else if (response!.ret === AppConst.API_RET_ALREADY_EXIST_DATA) {
              this.toastr.error(this.translate.instant('既に同じIDが登録済みです。'), '', { timeOut : 5000 });
            } else {
              console.error('api update error. ret='+response!.ret);
              this.toastr.error(this.translate.instant('登録できませんでした。'), '', { timeOut : 5000 });
            }
          }
        ).catch(
          error => {
            console.log(error);
            this.toastr.error(this.translate.instant('登録できませんでした。'), '', { timeOut : 5000 });
          }
        ).finally(
          () => {
            this.spinnerService.closeSpinner();
          }
        );
      }
    }
  }

  //展示名称ダイアログ
  openExhibitDetailNameDialog(): void {
    const dialogRef = this.dialog.open(MultiNameDialogComponent, {
      width: '60%',
      height: '90%',
      disableClose: true,
      data: { names: this.exhibitDetail!.exhibit_detail_names }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!!result) {
        this.exhibitDetail!.exhibit_detail_names = result;
        this.exhibitDetailNamesControl.setValue(this.exhibitDetail!.exhibit_detail_names);
      }
    });
  }

  //展示詳細ダイアログ
  openExhibitDetailDescDialog(): void {
    const dialogRef = this.dialog.open(MultiDescDialogComponent, {
      width: '60%',
      height: '90%',
      disableClose: true,
      data: { descs: this.exhibitDetail!.exhibit_detail_descs }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!!result) {
        this.exhibitDetail!.exhibit_detail_descs = result;
        this.exhibitDetailDescsControl.setValue(this.exhibitDetail!.exhibit_detail_descs);
      }
    });
  }

  //展示画像ファイル選択ダイアログ
  openSelectFilesDialog(): void {
    const dialogRef = this.dialog.open(SelectUploadedFileDialogComponent, {
      width: '80%',
      height: '90%',
      disableClose: true,
      data: { uploadedFile: null }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!!result && result.length > 0) {
        for (const row of result) {
          const lang = this.projectLanguages[0].lang_code;
          this.exhibitDetail!.exhibit_detail_files!.push({file_id: row.id, lang_code: lang});
        }
        this.getFiles();
      }
    });
  }

  deleteFiles(i: number): void {
    this.exhibitDetail!.exhibit_detail_files!.splice(i, 1);
    this.getFiles();
  }

  //Youtube埋め込みコードダイアログ
  openYoutubeCodeDialog(): void {
    const dialogRef = this.dialog.open(MultiYoutubeDialogComponent, {
      width: '60%',
      height: '90%',
      disableClose: true,
      data: { codes: this.exhibitDetail!.youtube_codes }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!!result) {
        this.exhibitDetail!.youtube_codes = result;
        this.youtubeCodesControl.setValue(this.exhibitDetail!.youtube_codes);
      }
    });
  }

  //外部リンク名称ダイアログ
  openExternallinkNameDialog(): void {
    const dialogRef = this.dialog.open(MultiNameDialogComponent, {
      width: '60%',
      height: '90%',
      disableClose: true,
      data: { names: this.exhibitDetail!.externallink_names }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!!result) {
        this.exhibitDetail!.externallink_names = result;
        this.externallinkNamesControl.setValue(this.exhibitDetail!.externallink_names);
      }
    });
  }

  //外部リンクＵＲＬダイアログ
  openExternallinkUrlDialog(): void {
    const dialogRef = this.dialog.open(MultiExternallinkDialogComponent, {
      width: '60%',
      height: '90%',
      disableClose: true,
      data: { codes: this.exhibitDetail!.externallink_urls }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!!result) {
        this.exhibitDetail!.externallink_urls = result;
        this.externallinkUrlsControl.setValue(this.exhibitDetail!.externallink_urls);
      }
    });
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

}
